/**
 * Hood / Capot (DI1/DI2) open-state detection from a GPS provider device object.
 *
 * Providers expose the digital-input state in many places (named sensors, raw
 * device fields, position attributes, or a JSON device_data/params blob). This
 * helper checks all of them and returns:
 *   true  → hood OPEN
 *   false → hood CLOSED
 *   null  → unknown (sensor not present / unreadable)
 */

const HOOD_TRUTHY = new Set(['1', 'on', 'open', 'opened', 'ouvert', 'true', 'high', 'yes', 'active']);
const HOOD_FALSY = new Set(['0', 'off', 'closed', 'close', 'ferme', 'fermé', 'false', 'low', 'no', 'inactive']);

export const interpretOpen = (v) => {
  if (v === true) return true;
  if (v === false) return false;
  if (v === null || v === undefined || v === '') return null;
  const s = String(v).trim().toLowerCase();
  if (HOOD_TRUTHY.has(s)) return true;
  if (HOOD_FALSY.has(s)) return false;
  const n = Number(s);
  return Number.isFinite(n) ? n > 0 : null;
};

export const HOOD_KEYS = ['di1', 'di2', 'din1', 'din2', 'in1', 'in2', 'input1', 'input2',
  'digital1', 'digital2', 'hood', 'capot', 'door', 'di_1', 'di_2'];

/**
 * Ignition / engine (ACC) ON-state from a GPS provider device object.
 * Reads the GPSWOX "acc" sensor (name "Ignition", value "On"/"Off", val
 * true/false) and falls back to engine_status / raw <ignition> field.
 *   true  → engine ON
 *   false → engine OFF
 *   null  → unknown
 */
export const getIgnitionOn = (device) => {
  if (!device) return null;
  const sensorLists = [device.sensors, device.raw && device.raw.sensors].filter(Array.isArray);
  for (const sensors of sensorLists) {
    for (const s of sensors) {
      const type = String(s.type || '').toLowerCase();
      const name = String(s.name || '').toLowerCase();
      if (type === 'acc' || type === 'ignition' || name.includes('ignition') || name.includes('contact')) {
        const st = interpretOpen(s.val !== undefined ? s.val : (s.value ?? s.state));
        if (st !== null) return st;
      }
    }
  }
  if (device.raw) {
    if (typeof device.raw.engine_status === 'boolean') return device.raw.engine_status;
    const other = device.raw.device_data && device.raw.device_data.traccar && device.raw.device_data.traccar.other;
    if (typeof other === 'string') {
      const m = other.match(/<ignition>(.*?)<\/ignition>/i);
      if (m) return interpretOpen(m[1]);
    }
  }
  return null;
};

export const getHoodOpen = (device, customName) => {
  if (!device) return null;
  const names = [customName, 'capot', 'hood', ...HOOD_KEYS]
    .filter(Boolean).map(s => String(s).toLowerCase());

  // 1) Named sensors (normalized device + raw provider object).
  const sensorLists = [device.sensors, device.raw && device.raw.sensors].filter(Array.isArray);
  for (const sensors of sensorLists) {
    for (const s of sensors) {
      const nm = `${s.name || ''} ${s.type || ''}`.toLowerCase();
      if (names.some(n => nm.includes(n))) {
        const st = interpretOpen(s.value ?? s.val ?? s.state ?? s.data);
        if (st !== null) return st;
      }
    }
  }

  // 2) Flat keys on the raw device / attributes / parsed device_data|params.
  const blobs = [];
  if (device.raw) blobs.push(device.raw);
  if (device.position && device.position.attributes) blobs.push(device.position.attributes);
  const parseMaybe = (x) => { try { return typeof x === 'string' ? JSON.parse(x) : x; } catch { return null; } };
  if (device.raw) {
    const dd = parseMaybe(device.raw.device_data); if (dd) blobs.push(dd);
    const pp = parseMaybe(device.raw.params); if (pp) blobs.push(pp);
  }
  for (const blob of blobs) {
    if (!blob || typeof blob !== 'object') continue;
    for (const [k, val] of Object.entries(blob)) {
      const key = k.toLowerCase();
      if (HOOD_KEYS.includes(key) || (customName && key === String(customName).toLowerCase())) {
        const st = interpretOpen(val);
        if (st !== null) return st;
      }
    }
  }

  // 3) Raw Teltonika protocol XML (device_data.traccar.other) — the digital
  //    inputs appear here as <io1>, <io2>, <io3>, <din1>… when the hood switch
  //    is wired. Also honour a custom field name (e.g. "io3") if provided.
  const other = device.raw && device.raw.device_data
    && device.raw.device_data.traccar && device.raw.device_data.traccar.other;
  if (typeof other === 'string') {
    const xmlKeys = ['io1', 'io2', 'io3', 'din1', 'din2', 'din3', 'hood', 'capot', 'door'];
    if (customName) xmlKeys.unshift(String(customName).toLowerCase());
    for (const key of xmlKeys) {
      const m = other.match(new RegExp(`<${key}>(.*?)</${key}>`, 'i'));
      if (m) {
        const st = interpretOpen(m[1]);
        if (st !== null) return st;
      }
    }
  }
  return null;
};
